/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from 'react';
import { supabase } from './supabase';

// ─── Local system-compliant primitives ────────────────────────────────────────
// Duplicated from Dashboard.js intentionally; a follow-up pass extracts both
// SegmentedPills + StatusPill to src/components/ so Home, Dashboard, and
// Requests share one source of truth.

function SegmentedPills({ value, onChange, options }) {
  return (
    <div
      className="inline-flex"
      style={{ background: 'var(--bg-hover)', border: '1px solid var(--border)', borderRadius: 6, padding: 2 }}
    >
      {options.map(opt => {
        const key = opt.key;
        const label = opt.label || opt.key;
        const active = value === key;
        return (
          <button
            key={key}
            title={opt.title}
            onClick={() => onChange(key)}
            className="transition-colors font-medium inline-flex items-center gap-1"
            style={{
              background: active ? 'var(--bg-card)' : 'transparent',
              color: active ? 'var(--text-primary)' : 'var(--text-secondary)',
              borderRadius: 6,
              padding: '6px 12px',
              fontSize: 14,
              border: 'none',
              cursor: 'pointer',
              boxShadow: active ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
            }}
            onMouseEnter={e => { if (!active) e.currentTarget.style.color = 'var(--text-primary)'; }}
            onMouseLeave={e => { if (!active) e.currentTarget.style.color = 'var(--text-secondary)'; }}
          >
            {label}
            {opt.count != null && opt.count > 0 && (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: active ? 'var(--text-primary)' : 'var(--text-secondary)',
                  background: active ? 'var(--bg-hover)' : 'var(--bg-subtle)',
                  borderRadius: 999,
                  padding: '1px 6px',
                  lineHeight: 1.4,
                }}
              >
                {opt.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function StatusPill({ tone = 'neutral', children }) {
  const palette = {
    green:   { background: 'var(--bg-success-subtle)',  color: 'var(--text-success)' },
    amber:   { background: 'var(--bg-warning-subtle)',  color: 'var(--text-warning)' },
    red:     { background: 'var(--bg-critical-subtle)', color: 'var(--text-critical)' },
    blue:    { background: 'var(--bg-info-subtle)',     color: 'var(--text-info)' },
    purple:  { background: 'var(--bg-accent-chip)',     color: 'var(--accent-primary)' },
    neutral: { background: 'var(--bg-subtle)',          color: 'var(--text-secondary)' },
  }[tone] || { background: 'var(--bg-subtle)', color: 'var(--text-secondary)' };
  return (
    <span
      className="inline-flex items-center text-[11px] font-medium"
      style={{ background: palette.background, color: palette.color, padding: '2px 8px', borderRadius: 6 }}
    >
      {children}
    </span>
  );
}

// Button variants. Primary = black, Primary-accent = purple, Secondary = white/border,
// Destructive = white/red-border. All: 8px radius, 8px 14px padding, 14/500.
function PrimaryBtn({ onClick, disabled, children, className = '' }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`text-sm font-medium transition-colors disabled:opacity-50 ${className}`}
      style={{ background: 'var(--text-primary)', color: 'var(--bg-card)', border: 'none', padding: '8px 14px', borderRadius: 8, cursor: disabled ? 'not-allowed' : 'pointer' }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.background = '#262626'; }}
      onMouseLeave={e => { if (!disabled) e.currentTarget.style.background = 'var(--text-primary)'; }}
    >
      {children}
    </button>
  );
}

function PrimaryAccentBtn({ onClick, disabled, children, className = '' }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`text-sm font-medium transition-colors disabled:opacity-50 ${className}`}
      style={{ background: 'var(--accent-primary)', color: '#FFFFFF', border: 'none', padding: '8px 14px', borderRadius: 8, cursor: disabled ? 'not-allowed' : 'pointer' }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.background = 'var(--accent-primary-hover)'; }}
      onMouseLeave={e => { if (!disabled) e.currentTarget.style.background = 'var(--accent-primary)'; }}
    >
      {children}
    </button>
  );
}

function SecondaryBtn({ onClick, disabled, children, className = '' }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`text-sm font-medium transition-colors disabled:opacity-50 ${className}`}
      style={{ background: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border)', padding: '8px 14px', borderRadius: 8, cursor: disabled ? 'not-allowed' : 'pointer' }}
      onMouseEnter={e => { if (!disabled) { e.currentTarget.style.background = 'var(--bg-hover)'; e.currentTarget.style.borderColor = 'var(--border-strong)'; } }}
      onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg-card)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
    >
      {children}
    </button>
  );
}

function DestructiveBtn({ onClick, disabled, children, className = '' }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`text-sm font-medium transition-colors disabled:opacity-50 ${className}`}
      style={{ background: 'var(--bg-card)', color: 'var(--text-critical)', border: '1px solid var(--text-critical)', padding: '8px 14px', borderRadius: 8, cursor: disabled ? 'not-allowed' : 'pointer' }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.background = 'var(--bg-critical-subtle)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg-card)'; }}
    >
      {children}
    </button>
  );
}

// ─── Semantics ────────────────────────────────────────────────────────────────
// Type colors encode CATEGORY (what kind of request). Status colors encode OUTCOME.
// See DESIGN_SYSTEM.md "Request type semantics".
function typeTone(type) {
  if (type === 'time_off' || type === 'Time off') return 'purple';
  if (type === 'shift_release') return 'amber';
  if (type === 'shift_pickup') return 'blue';
  if (type === 'Shift swap' || type === 'swap') return 'blue';
  if (type === 'More hours') return 'green';
  return 'neutral';
}

function typeLabel(type) {
  if (type === 'time_off' || type === 'Time off') return 'Time off';
  if (type === 'shift_release') return 'Shift release';
  if (type === 'shift_pickup') return 'Shift pickup';
  return type;
}

function statusTone(status) {
  if (status === 'approved') return 'green';
  if (status === 'denied') return 'red';
  return 'amber'; // pending
}

function statusDotColor(status) {
  if (status === 'approved') return 'var(--text-success)';
  if (status === 'denied') return 'var(--text-muted)';
  return 'var(--text-warning)';
}

function swapStatusTone(status) {
  if (status === 'pending_manager') return 'blue';
  if (status === 'pending_employee') return 'amber';
  if (status === 'approved') return 'green';
  if (status === 'denied') return 'red';
  return 'neutral';
}

function swapStatusLabel(status) {
  if (status === 'pending_manager') return 'Awaiting approval';
  if (status === 'pending_employee') return 'Awaiting response';
  if (status === 'approved') return 'Completed';
  if (status === 'denied') return 'Denied';
  return 'Cancelled';
}

export default function Requests({ companyId, onRequestHandled }) {
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [viewMode, setViewMode] = useState('list');
  const [calMonth, setCalMonth] = useState(() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1); });
  const [selectedDay, setSelectedDay] = useState(null);
  const [detailRequest, setDetailRequest] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [typeFilter, setTypeFilter] = useState('all');
  const [swapRequests, setSwapRequests] = useState([]);
  const [swapDenyNote, setSwapDenyNote] = useState('');
  const [swapDenyId, setSwapDenyId] = useState(null);

  React.useEffect(() => {
    fetchRequests();
    fetchSwaps();
    if (companyId) {
      supabase.from('employees').select('*').eq('company_id', companyId).then(({ data }) => { if (data) setEmployees(data); });
    }
  }, [companyId]);

  async function fetchRequests() {
    if (!companyId) return;
    const { data } = await supabase.from('requests').select('*').eq('company_id', companyId);
    if (data) setRequests(data);
  }

  async function fetchSwaps() {
    if (!companyId) return;
    const { data } = await supabase.from('swap_requests').select('*').eq('company_id', companyId).order('created_at', { ascending: false });
    setSwapRequests(data || []);
  }

  async function sendSMS(to, body) {
    if (!to) return;
    try {
      await fetch('/api/send-sms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to, body }),
      });
    } catch (e) { console.error('SMS send failed:', e); }
  }

  async function approveSwap(swapId) {
    const swap = swapRequests.find(s => s.id === swapId);
    if (!swap) return;
    if (swap.type === 'pickup') {
      await supabase.from('shifts').update({ employee_id: swap.requester_id }).eq('id', swap.target_shift_id);
      await supabase.from('swap_requests').update({ status: 'approved' }).eq('id', swapId);
      const msg = `Shift pickup approved! ${swap.requester_name} now has ${swap.target_shift_detail} (from ${swap.target_name}).`;
      await supabase.from('messages').insert([
        { company_id: companyId, sender_id: 0, recipient_id: swap.requester_id, message: msg, read: false },
        { company_id: companyId, sender_id: 0, recipient_id: swap.target_id, message: `${swap.requester_name} has taken your shift: ${swap.target_shift_detail}. Approved by manager.`, read: false },
      ]);
    } else {
      await supabase.from('shifts').update({ employee_id: swap.target_id }).eq('id', swap.requester_shift_id);
      await supabase.from('shifts').update({ employee_id: swap.requester_id }).eq('id', swap.target_shift_id);
      await supabase.from('swap_requests').update({ status: 'approved' }).eq('id', swapId);
      const msg = `Your shift swap has been approved! ${swap.requester_name}: ${swap.target_shift_detail} \u21C4 ${swap.target_name}: ${swap.requester_shift_detail}`;
      await supabase.from('messages').insert([
        { company_id: companyId, sender_id: 0, recipient_id: swap.requester_id, message: msg, read: false },
        { company_id: companyId, sender_id: 0, recipient_id: swap.target_id, message: msg, read: false },
      ]);
    }
    const reqEmp = employees.find(e => String(e.id) === String(swap.requester_id));
    const tgtEmp = employees.find(e => String(e.id) === String(swap.target_id));
    if (reqEmp?.phone) sendSMS(reqEmp.phone, `Posted: Your shift swap has been approved!`);
    if (tgtEmp?.phone) sendSMS(tgtEmp.phone, `Posted: A shift swap involving your schedule has been approved. Check the app for details.`);
    fetchSwaps();
    if (onRequestHandled) onRequestHandled();
  }

  async function denySwap(swapId, note) {
    const swap = swapRequests.find(s => s.id === swapId);
    if (!swap) return;
    await supabase.from('swap_requests').update({ status: 'denied' }).eq('id', swapId);
    const msg = `Your shift swap was denied by the manager.${note ? ' Reason: ' + note : ''}`;
    await supabase.from('messages').insert([
      { company_id: companyId, sender_id: 0, recipient_id: swap.requester_id, message: msg, read: false },
      { company_id: companyId, sender_id: 0, recipient_id: swap.target_id, message: msg, read: false },
    ]);
    const reqEmp = employees.find(e => String(e.id) === String(swap.requester_id));
    const tgtEmp = employees.find(e => String(e.id) === String(swap.target_id));
    if (reqEmp?.phone) sendSMS(reqEmp.phone, `Posted: Your shift swap was denied.${note ? ' Reason: ' + note : ''}`);
    if (tgtEmp?.phone) sendSMS(tgtEmp.phone, `Posted: A shift swap request was denied.`);
    setSwapDenyId(null); setSwapDenyNote('');
    fetchSwaps();
    if (onRequestHandled) onRequestHandled();
  }

  async function approve(id) {
    const req = requests.find(r => r.id === id);
    await supabase.from('requests').update({ status: 'approved' }).eq('id', id);
    if (req && req.type === 'shift_release' && companyId) {
      const hasIncentive = req.detail && req.detail.includes('$');
      const incentiveText = hasIncentive ? req.detail.split('—')[1]?.trim() || '' : '';
      await supabase.from('messages').insert([{
        company_id: companyId,
        sender_id: 0,
        recipient_id: 0,
        message: `📢 Open shift available! ${req.detail}${incentiveText ? `\n💰 ${incentiveText}` : ''}\nApproved by manager — pick it up in the Open Shifts tab.`,
        read: false,
      }]);
    }
    if (req && req.type === 'shift_pickup' && companyId) {
      const dateMatch = req.detail?.match(/\d{4}-\d{2}-\d{2}/);
      const periodMatch = req.detail?.match(/Pickup (\w[\w\s]*?) on/);
      const roleMatch = req.detail?.match(/\(([^)]+)\)/);
      const fromMatch = req.detail?.match(/from (.+)$/);
      const emp = employees.find(e => e.name === req.employee_name);
      if (emp && dateMatch) {
        const shiftDate = dateMatch[0];
        const shiftName = periodMatch ? periodMatch[1] : null;
        const role = roleMatch ? roleMatch[1] : null;
        const fromName = fromMatch ? fromMatch[1] : null;
        const fromEmp = fromName ? employees.find(e => e.name === fromName) : null;
        let matchShift = null;
        if (fromEmp && shiftName) {
          const { data } = await supabase.from('shifts').select('id').eq('company_id', companyId).eq('day', shiftDate).eq('shift_name', shiftName).eq('employee_id', fromEmp.id).limit(1);
          if (data?.length) matchShift = data[0];
        }
        if (!matchShift && shiftName && role) {
          const { data } = await supabase.from('shifts').select('id').eq('company_id', companyId).eq('day', shiftDate).eq('shift_name', shiftName).eq('role', role).limit(1);
          if (data?.length) matchShift = data[0];
        }
        if (matchShift) {
          const { error: updateErr } = await supabase.from('shifts').update({ employee_id: emp.id }).eq('id', matchShift.id);
          console.log('SHIFT PICKUP ASSIGN:', { shiftId: matchShift.id, newEmployee: emp.id, error: updateErr });
        } else {
          console.warn('SHIFT PICKUP: could not find matching shift', { shiftDate, shiftName, role, fromName });
        }
        if (matchShift) {
          await supabase.from('shift_releases').update({ status: 'covered' }).eq('shift_id', matchShift.id).in('status', ['open', 'claimed']);
        }
        const shiftDate2 = dateMatch[0];
        const fromEmp2 = fromName ? employees.find(e => e.name === fromName) : null;
        if (fromEmp2 && shiftName) {
          await supabase.from('shift_releases').update({ status: 'covered' }).eq('employee_id', fromEmp2.id).eq('shift_date', shiftDate2).eq('shift_name', shiftName).in('status', ['open', 'claimed']);
        }
      }
      await supabase.from('messages').insert([{
        company_id: companyId, sender_id: 0, recipient_id: 0,
        message: `✅ ${req.employee_name}'s shift pickup approved: ${req.detail}`,
        read: false,
      }]);
    }
    if (req) {
      const emp = employees.find(e => e.name === req.employee_name);
      if (emp?.phone) {
        const typeMsg = req.type === 'time_off' || req.type === 'Time off'
          ? `Your time-off request has been approved.`
          : req.type === 'shift_pickup'
          ? `Your shift pickup has been approved! Check the app for details.`
          : req.type === 'shift_release'
          ? `Your shift release has been approved and posted for others.`
          : `Your request has been approved.`;
        sendSMS(emp.phone, `Posted: ${typeMsg}`);
      }
    }
    fetchRequests();
    if (onRequestHandled) onRequestHandled();
  }

  async function deny(id) {
    const req = requests.find(r => r.id === id);
    await supabase.from('requests').update({ status: 'denied' }).eq('id', id);
    if (req) {
      const emp = employees.find(e => e.name === req.employee_name);
      if (emp?.phone) sendSMS(emp.phone, `Posted: Your request has been denied. Contact your manager for details.`);
    }
    fetchRequests();
    if (onRequestHandled) onRequestHandled();
  }

  function parseDatesFromRequest(r) {
    if (Array.isArray(r.dates) && r.dates.length > 0) return r.dates;
    const text = (r.detail || '') + ' ' + (r.note || '');
    const matches = text.match(/\d{4}-\d{2}-\d{2}/g);
    return matches || [];
  }

  const timeOffRequests = requests.filter(r => (r.type === 'time_off' || r.type === 'Time off')).map(r => ({ ...r, _dates: parseDatesFromRequest(r) })).filter(r => r._dates.length > 0);

  function getRequestsForDate(dateStr) {
    return timeOffRequests.filter(r => r._dates.includes(dateStr));
  }

  function getCalendarDays(monthDate) {
    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < (firstDay === 0 ? 6 : firstDay - 1); i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
    return cells;
  }

  function fmtDate(d) { return d.toISOString().split('T')[0]; }

  const filtered = requests.filter(r => {
    if (filter !== 'all' && r.status !== filter) return false;
    if (typeFilter !== 'all' && r.type !== typeFilter) return false;
    return true;
  });
  const pendingCount = requests.filter(r => r.status === 'pending').length;
  const approvedThisMonth = requests.filter(r => r.status === 'approved').length;
  const upcomingApproved = timeOffRequests.filter(r => r.status === 'approved' && r._dates.some(d => d >= fmtDate(new Date()))).length;

  const selectedDayRequests = selectedDay ? getRequestsForDate(selectedDay) : [];

  const kpis = [
    { label: 'Pending', val: pendingCount, color: 'var(--text-warning)' },
    { label: 'Approved this month', val: approvedThisMonth, color: 'var(--text-success)' },
    { label: 'Upcoming approved', val: upcomingApproved, color: 'var(--text-secondary)' },
  ];

  return (
    <div className="flex-1 overflow-y-auto p-6 bg-gray-50">

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900" style={{ letterSpacing: '-0.01em' }}>Staff requests</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>{pendingCount} pending approval</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <SegmentedPills
            value={viewMode}
            onChange={setViewMode}
            options={[
              { key: 'list', label: 'List' },
              { key: 'calendar', label: 'Calendar' },
            ]}
          />
          {viewMode === 'list' && (
            <>
              <SegmentedPills
                value={filter}
                onChange={setFilter}
                options={[
                  { key: 'pending', label: 'Pending', count: pendingCount },
                  { key: 'all', label: 'History' },
                ]}
              />
              <SegmentedPills
                value={typeFilter}
                onChange={setTypeFilter}
                options={[
                  { key: 'all', label: 'All types' },
                  { key: 'shift_release', label: 'Releases' },
                  { key: 'shift_pickup', label: 'Pickups' },
                  { key: 'time_off', label: 'Time off' },
                  { key: 'swap', label: 'Swaps' },
                ]}
              />
            </>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        {kpis.map((s, i) => (
          <div
            key={i}
            className="rounded-lg bg-white p-5"
            style={{ border: '1px solid var(--border)' }}
          >
            <div
              style={{ fontSize: 12, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 8 }}
            >
              {s.label}
            </div>
            <div
              style={{ fontSize: 32, fontWeight: 700, letterSpacing: '-0.02em', color: s.color, lineHeight: 1.1 }}
            >
              {s.val}
            </div>
          </div>
        ))}
      </div>

      {/* ─── LIST VIEW ─── */}
      {viewMode === 'list' && (() => {
        const hasSwapResults = swapRequests.some(s => {
          if (typeFilter === 'shift_pickup' && s.type !== 'pickup') return false;
          if (typeFilter === 'swap' && s.type !== 'swap') return false;
          if (filter === 'pending') return s.status === 'pending_manager';
          if (filter !== 'all') return s.status === filter;
          return true;
        });
        const showEmptyState = filtered.length === 0 && !hasSwapResults && typeFilter !== 'swap';
        const hideRequestsList = typeFilter === 'swap';
        return !hideRequestsList ? (
          <div
            className="rounded-lg bg-white overflow-hidden"
            style={{ border: '1px solid var(--border)' }}
          >
            {showEmptyState && (
              <div className="flex flex-col items-center justify-center text-center" style={{ padding: '48px 24px', color: 'var(--text-muted)' }}>
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 12, opacity: 0.6 }}>
                  <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
                  <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
                </svg>
                <div className="text-sm">No requests found.</div>
              </div>
            )}
            {filtered.map((r, i) => (
              <div
                key={r.id}
                className="flex items-center justify-between transition-colors"
                style={{
                  padding: '12px 16px',
                  minHeight: 52,
                  borderBottom: i < filtered.length - 1 ? '1px solid var(--border)' : 'none',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-subtle)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0 ${r.color}`}>{r.initials}</div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-gray-900">{r.employee_name || r.name}</span>
                      <StatusPill tone={typeTone(r.type)}>{typeLabel(r.type)}</StatusPill>
                      {r.type === 'shift_release' && r.detail?.includes('$') && (
                        <StatusPill tone="green">💰 Incentive</StatusPill>
                      )}
                    </div>
                    <div className="mt-0.5" style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{r.detail}</div>
                    {r.type === 'shift_pickup' && (() => {
                      const emp = employees.find(e => e.name === r.employee_name);
                      if (!emp) return null;
                      return <div className="mt-0.5" style={{ fontSize: 12, color: 'var(--text-info)' }}>Target: {emp.target_hours || 30}h/wk · Max: {emp.max_hours_per_week || 40}h/wk</div>;
                    })()}
                    {r.note && <div className="italic mt-0.5" style={{ fontSize: 12, color: 'var(--text-muted)' }}>"{r.note}"</div>}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {r.type === 'shift_release' && (
                    <SecondaryBtn onClick={() => setDetailRequest(r)}>Details</SecondaryBtn>
                  )}
                  <StatusPill tone={statusTone(r.status)}>{r.status}</StatusPill>
                  {r.status === 'pending' && (
                    <>
                      <PrimaryBtn onClick={() => approve(r.id)}>Approve</PrimaryBtn>
                      <DestructiveBtn onClick={() => deny(r.id)}>Decline</DestructiveBtn>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : null;
      })()}

      {/* ─── SWAP REQUESTS ─── */}
      {viewMode === 'list' && (typeFilter === 'all' || typeFilter === 'swap' || typeFilter === 'shift_pickup') && swapRequests.filter(s => {
        if (typeFilter === 'shift_pickup' && s.type !== 'pickup') return false;
        if (typeFilter === 'swap' && s.type !== 'swap') return false;
        if (filter === 'pending') return s.status === 'pending_manager';
        if (filter !== 'all') return s.status === filter;
        return true;
      }).length > 0 && (
        <div
          className="rounded-lg bg-white overflow-hidden mt-6"
          style={{ border: '1px solid var(--border)' }}
        >
          <div
            className="px-4 py-3 flex items-center justify-between"
            style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)' }}
          >
            <div style={{ fontSize: 12, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)' }}>Shift swaps & pickups</div>
            <StatusPill tone="blue">{swapRequests.filter(s => s.status === 'pending_manager').length} awaiting approval</StatusPill>
          </div>
          {swapRequests.filter(s => {
            if (typeFilter === 'shift_pickup' && s.type !== 'pickup') return false;
            if (typeFilter === 'swap' && s.type !== 'swap') return false;
            if (filter === 'pending') return s.status === 'pending_manager';
            if (filter !== 'all') return s.status === filter;
            return true;
          }).map((swap, i, arr) => {
            const requester = employees.find(e => String(e.id) === String(swap.requester_id));
            const target = employees.find(e => String(e.id) === String(swap.target_id));
            const reqRoles = requester ? (typeof requester.role === 'string' ? requester.role.split(',').map(r => r.trim()) : []) : [];
            const tgtRoles = target ? (typeof target.role === 'string' ? target.role.split(',').map(r => r.trim()) : []) : [];
            const isPickup = swap.type === 'pickup';
            const reqShiftRole = swap.target_shift_role;
            const tgtShiftRole = swap.requester_shift_role;
            const crossRoleWarning = !isPickup && reqShiftRole && tgtShiftRole && reqShiftRole !== tgtShiftRole;
            const reqQualified = !reqShiftRole || reqRoles.includes(reqShiftRole);
            const tgtQualified = isPickup || !tgtShiftRole || tgtRoles.includes(tgtShiftRole);
            const targetShiftLabel = `${swap.target_shift_period} on ${swap.target_shift_day} (${swap.target_shift_role})`;
            const requesterShiftLabel = swap.requester_shift_period ? `${swap.requester_shift_period} on ${swap.requester_shift_day} (${swap.requester_shift_role})` : '';
            return (
              <div
                key={swap.id}
                className="px-4 py-4"
                style={{ borderBottom: i < arr.length - 1 ? '1px solid var(--border)' : 'none' }}
              >
                {isPickup ? (
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${requester?.color || 'bg-gray-100 text-gray-600'}`}>{requester?.initials || '?'}</div>
                    <div className="text-xs flex-1"><div className="font-semibold text-gray-900">{swap.requester_name}</div><div style={{ color: 'var(--text-secondary)' }}>wants to take shift</div></div>
                    <div className="text-lg" style={{ color: 'var(--text-muted)' }}>&#x2192;</div>
                    <div className="text-xs flex-1 text-right"><div className="font-semibold text-gray-900">{targetShiftLabel}</div><div style={{ color: 'var(--text-secondary)' }}>from {swap.target_name}</div></div>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${target?.color || 'bg-gray-100 text-gray-600'}`}>{target?.initials || '?'}</div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex items-center gap-2 flex-1">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${requester?.color || 'bg-gray-100 text-gray-600'}`}>{requester?.initials || '?'}</div>
                      <div className="text-xs"><div className="font-semibold text-gray-900">{swap.requester_name}</div><div style={{ color: 'var(--text-secondary)' }}>{requesterShiftLabel}</div></div>
                    </div>
                    <div className="text-lg" style={{ color: 'var(--text-muted)' }}>&#x21C4;</div>
                    <div className="flex items-center gap-2 flex-1 justify-end text-right">
                      <div className="text-xs"><div className="font-semibold text-gray-900">{swap.target_name}</div><div style={{ color: 'var(--text-secondary)' }}>{targetShiftLabel}</div></div>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${target?.color || 'bg-gray-100 text-gray-600'}`}>{target?.initials || '?'}</div>
                    </div>
                  </div>
                )}
                {swap.status === 'pending_manager' && (
                  <div className="text-xs mb-2" style={{ color: 'var(--text-success)' }}>Both employees have agreed.</div>
                )}
                {!reqQualified && (
                  <div className="text-xs rounded-md p-2 mb-2 font-medium" style={{ background: 'var(--bg-critical-subtle)', border: '1px solid var(--border)', color: 'var(--text-critical)' }}>
                    &#x26D4; {swap.requester_name} is not qualified for {reqShiftRole}
                  </div>
                )}
                {crossRoleWarning && (
                  <div className="text-xs rounded-md p-2 mb-2" style={{ background: 'var(--bg-warning-subtle)', border: '1px solid var(--border)', color: 'var(--text-warning)' }}>
                    &#x26A0;&#xFE0F; Cross-role swap: {swap.requester_name} would work as {reqShiftRole}, {swap.target_name} would work as {tgtShiftRole}
                    {!tgtQualified && <div className="font-medium mt-1" style={{ color: 'var(--text-critical)' }}>&#x26D4; {swap.target_name} is not qualified for {tgtShiftRole}</div>}
                  </div>
                )}
                {swap.note && (
                  <div className="text-xs italic mb-2" style={{ color: 'var(--text-muted)' }}>"{swap.note}"</div>
                )}
                <div className="flex items-center gap-2">
                  <StatusPill tone={swapStatusTone(swap.status)}>{swapStatusLabel(swap.status)}</StatusPill>
                  {swap.status === 'pending_manager' && (
                    <div className="flex gap-2 ml-auto items-center">
                      <PrimaryBtn onClick={() => approveSwap(swap.id)} disabled={!reqQualified || !tgtQualified}>Approve</PrimaryBtn>
                      {swapDenyId === swap.id ? (
                        <div className="flex items-center gap-1">
                          <input
                            value={swapDenyNote}
                            onChange={e => setSwapDenyNote(e.target.value)}
                            placeholder="Reason (optional)"
                            className="rounded-lg text-sm w-40 focus:outline-none"
                            style={{ border: '1px solid var(--border)', padding: '8px 10px', background: 'var(--bg-card)', color: 'var(--text-primary)' }}
                            onFocus={e => { e.currentTarget.style.borderColor = 'var(--accent-primary)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(109, 40, 217, 0.1)'; }}
                            onBlur={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
                            autoFocus
                          />
                          <PrimaryBtn onClick={() => denySwap(swap.id, swapDenyNote)}>Confirm decline</PrimaryBtn>
                          <button onClick={() => { setSwapDenyId(null); setSwapDenyNote(''); }} className="text-xs px-1" style={{ color: 'var(--text-secondary)' }}>&#x2715;</button>
                        </div>
                      ) : (
                        <DestructiveBtn onClick={() => setSwapDenyId(swap.id)}>Decline</DestructiveBtn>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ─── CALENDAR VIEW ─── */}
      {viewMode === 'calendar' && (
        <div className="flex gap-4">
          <div
            className="flex-1 rounded-lg bg-white overflow-hidden"
            style={{ border: '1px solid var(--border)' }}
          >
            <div
              className="flex items-center justify-between px-4 py-3"
              style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-subtle)' }}
            >
              <button
                onClick={() => setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth() - 1, 1))}
                className="text-sm px-2 rounded-md transition-colors"
                style={{ color: 'var(--text-secondary)', background: 'transparent', border: 'none', cursor: 'pointer' }}
                onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-primary)'; e.currentTarget.style.background = 'var(--bg-hover)'; }}
                onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-secondary)'; e.currentTarget.style.background = 'transparent'; }}
              >←</button>
              <div className="text-sm font-semibold text-gray-900">{calMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</div>
              <button
                onClick={() => setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth() + 1, 1))}
                className="text-sm px-2 rounded-md transition-colors"
                style={{ color: 'var(--text-secondary)', background: 'transparent', border: 'none', cursor: 'pointer' }}
                onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-primary)'; e.currentTarget.style.background = 'var(--bg-hover)'; }}
                onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-secondary)'; e.currentTarget.style.background = 'transparent'; }}
              >→</button>
            </div>
            <div className="grid grid-cols-7" style={{ borderBottom: '1px solid var(--border)' }}>
              {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d => (
                <div key={d} className="text-center py-2" style={{ fontSize: 11, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)' }}>{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {getCalendarDays(calMonth).map((d, i) => {
                if (!d) return <div key={i} style={{ borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)', minHeight: 80 }} />;
                const ds = fmtDate(d);
                const dayReqs = getRequestsForDate(ds);
                const isToday = ds === fmtDate(new Date());
                const isSel = selectedDay === ds;
                return (
                  <div
                    key={i}
                    onClick={() => setSelectedDay(ds)}
                    className="cursor-pointer transition-colors"
                    style={{
                      borderBottom: '1px solid var(--border)',
                      borderRight: '1px solid var(--border)',
                      borderLeft: isSel ? '2px solid var(--accent-primary)' : 'none',
                      background: isSel ? 'var(--accent-subtle)' : 'transparent',
                      minHeight: 80,
                      padding: 4,
                    }}
                    onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = 'var(--accent-subtle)'; }}
                    onMouseLeave={e => { if (!isSel) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 4, color: isToday ? 'var(--accent-primary)' : 'var(--text-secondary)' }}>{d.getDate()}</div>
                    <div className="flex flex-col gap-0.5">
                      {dayReqs.slice(0, 3).map((r, ri) => (
                        <div key={ri} className="flex items-center gap-1">
                          <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: statusDotColor(r.status) }} />
                          <span className="text-xs truncate" style={{ color: 'var(--text-primary)' }}>{(r.employee_name || r.name || '').split(' ')[0]}</span>
                        </div>
                      ))}
                      {dayReqs.length > 3 && <div className="text-xs" style={{ color: 'var(--text-muted)' }}>+{dayReqs.length - 3} more</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {selectedDay && (
            <div
              className="w-72 rounded-lg bg-white flex-shrink-0 overflow-hidden"
              style={{ border: '1px solid var(--border)' }}
            >
              <div
                className="px-4 py-3"
                style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-subtle)' }}
              >
                <div className="text-sm font-semibold text-gray-900">
                  {new Date(selectedDay + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                </div>
                <div className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>{selectedDayRequests.length} request{selectedDayRequests.length !== 1 ? 's' : ''}</div>
              </div>
              <div className="p-3">
                {selectedDayRequests.length === 0 ? (
                  <div className="text-sm text-center py-6" style={{ color: 'var(--text-muted)' }}>No time off requests</div>
                ) : (
                  <div className="flex flex-col gap-2">
                    {selectedDayRequests.map(r => (
                      <div
                        key={r.id}
                        className="rounded-md p-3"
                        style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border)' }}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${r.color}`}>{r.initials}</div>
                          <span className="text-sm font-semibold text-gray-900">{r.employee_name || r.name}</span>
                        </div>
                        {r.note && <div className="text-xs italic mb-2" style={{ color: 'var(--text-muted)' }}>"{r.note}"</div>}
                        <div className="flex items-center gap-2 flex-wrap">
                          <StatusPill tone={statusTone(r.status)}>{r.status}</StatusPill>
                          {r.status === 'pending' && (
                            <>
                              <PrimaryBtn onClick={() => approve(r.id)} className="!text-xs !px-2.5 !py-1">Approve</PrimaryBtn>
                              <DestructiveBtn onClick={() => deny(r.id)} className="!text-xs !px-2.5 !py-1">Decline</DestructiveBtn>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* SHIFT RELEASE DETAIL MODAL */}
      {detailRequest && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
          <div
            className="rounded-lg bg-white p-6 w-full max-w-md mx-4 max-h-screen overflow-y-auto"
            style={{ border: '1px solid var(--border)', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="text-base font-semibold text-gray-900">Shift release details</div>
              <button
                onClick={() => setDetailRequest(null)}
                className="text-lg px-1 rounded-md transition-colors"
                style={{ color: 'var(--text-secondary)', background: 'transparent', border: 'none', cursor: 'pointer' }}
                onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-primary)'; e.currentTarget.style.background = 'var(--bg-hover)'; }}
                onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-secondary)'; e.currentTarget.style.background = 'transparent'; }}
              >✕</button>
            </div>

            <div className="rounded-md p-4 mb-4" style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border)' }}>
              <div className="flex items-center gap-3 mb-2">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-medium ${detailRequest.color}`}>{detailRequest.initials}</div>
                <div>
                  <div className="text-sm font-semibold text-gray-900">{detailRequest.employee_name}</div>
                  <StatusPill tone={typeTone(detailRequest.type)}>{typeLabel(detailRequest.type)}</StatusPill>
                </div>
              </div>
              <div className="text-sm mt-2" style={{ color: 'var(--text-primary)' }}>{detailRequest.detail}</div>
              {detailRequest.note && <div className="text-xs italic mt-1" style={{ color: 'var(--text-muted)' }}>"{detailRequest.note}"</div>}
              {detailRequest.detail?.includes('$') && (
                <div className="mt-2 rounded-md px-3 py-2 text-xs" style={{ background: 'var(--bg-success-subtle)', border: '1px solid var(--border)', color: 'var(--text-success)' }}>
                  💰 Employee is offering incentive pay for coverage
                </div>
              )}
            </div>

            <div className="mb-4">
              <div style={{ fontSize: 12, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 8 }}>Best fit to cover</div>
              {(() => {
                const roleMatch = detailRequest.detail?.match(/\(([^)]+)\)/);
                const role = roleMatch ? roleMatch[1] : null;
                const dateMatch = detailRequest.detail?.match(/\d{4}-\d{2}-\d{2}/);
                const shiftDate = dateMatch ? dateMatch[0] : null;
                const dayName = shiftDate ? ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][new Date(shiftDate + 'T12:00:00').getDay()] : null;

                const candidates = employees.filter(e => {
                  if (e.name === detailRequest.employee_name) return false;
                  if (!role) return true;
                  const roles = typeof e.role === 'string' ? e.role.split(',').map(r => r.trim()) : (e.role || []);
                  if (!roles.includes(role)) return false;
                  if (dayName) {
                    const avail = (e.availability || '').split(',').map(d => d.trim());
                    if (avail.length > 0 && !avail.includes(dayName)) return false;
                  }
                  return true;
                }).slice(0, 6);

                return candidates.length === 0 ? (
                  <div className="text-xs text-center py-3" style={{ color: 'var(--text-muted)' }}>No available candidates found</div>
                ) : (
                  <div className="flex flex-col gap-1.5">
                    {candidates.map((c, ci) => (
                      <div
                        key={c.id}
                        className="flex items-center gap-2.5 px-3 py-2 rounded-md"
                        style={{
                          background: ci === 0 ? 'var(--bg-success-subtle)' : 'var(--bg-card)',
                          border: '1px solid var(--border)',
                        }}
                      >
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0 ${c.color}`}>{c.initials}</div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900">{c.name}</div>
                          <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>{typeof c.role === 'string' ? c.role : (c.role || []).join(', ')}</div>
                        </div>
                        {ci === 0 && <StatusPill tone="green">Best fit</StatusPill>}
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>

            {detailRequest.status === 'pending' && (
              <div className="flex gap-2">
                <PrimaryAccentBtn
                  onClick={() => { approve(detailRequest.id); setDetailRequest(null); }}
                  className="flex-1"
                >
                  Approve & announce to team
                </PrimaryAccentBtn>
                <DestructiveBtn
                  onClick={() => { deny(detailRequest.id); setDetailRequest(null); }}
                  className="flex-1"
                >
                  Decline
                </DestructiveBtn>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
